import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Stack,
  Theme,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import EventOutlined from '@mui/icons-material/EventOutlined';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import { format, formatDistanceStrict, formatDistanceToNow } from 'date-fns';
import useConfirm from '../../hooks/useConfirm';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useCancelOffboarding from '../../hooks/offboardings/useCancelOffboarding';
import useGetOffboarding from '../../hooks/offboardings/useGetOffboarding';
import { Offboarding } from '../../types/offboardings';
import { ROUTES } from '../../constants/global';
import OffboardingCampaignActionMenu from './OffboardingCampaignActionMenu';

const RACE_BUFFER_SECONDS = 60;

interface Props {
  offboarding: Offboarding;
  employeeId: string;
  employeeName: string;
  businessId: string;
  onCompleted?: () => void;
}

const enterIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulseDot = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(255, 48, 8, 0.45); }
  70%  { box-shadow: 0 0 0 8px rgba(255, 48, 8, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 48, 8, 0); }
`;

function scheduledMs(offboarding: Offboarding): number | null {
  return offboarding.scheduledFor
    ? new Date(offboarding.scheduledFor).getTime()
    : null;
}

function OffboardingBanner({
  offboarding: initialOffboarding,
  employeeId,
  employeeName,
  businessId,
  onCompleted,
}: Props) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const initialMs = scheduledMs(initialOffboarding);
  const isInProgress =
    !initialOffboarding.completedAt &&
    !initialOffboarding.canceledAt &&
    !!initialMs &&
    initialMs <= now;

  const { data: polledOffboarding, pollExhausted } = useGetOffboarding(
    initialOffboarding.id,
    {
      poll: isInProgress,
      businessId,
      includeSourceCampaign: true,
      onCompleted: () => {
        toast.success('Artist has been offboarded');
        onCompleted?.();
      },
    },
  );

  const offboarding = polledOffboarding ?? initialOffboarding;
  const liveMs = scheduledMs(offboarding);
  const isFutureScheduled =
    !offboarding.completedAt &&
    !offboarding.canceledAt &&
    !!liveMs &&
    liveMs > now;
  const inRaceWindow =
    isFutureScheduled && !!liveMs && liveMs - now <= RACE_BUFFER_SECONDS * 1000;
  const showInProgress =
    !offboarding.completedAt &&
    !offboarding.canceledAt &&
    !!liveMs &&
    liveMs <= now;

  const cancel = useCancelOffboarding({
    onMutate: () =>
      openLoadingModal({
        header: 'Canceling offboarding',
        message: 'Please wait…',
      }),
    onSettled: closeLoadingModal,
    onSuccess: () => {
      toast.success('Offboarding canceled');
      onCompleted?.();
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? 'Failed to cancel offboarding';
      toast.error(message);
    },
  });

  const onEdit = () => navigate(`${ROUTES.team}/${employeeId}/offboard`);
  const onCancel = async () => {
    const confirmed = await confirm({
      title: 'Cancel offboarding?',
      message:
        'The scheduled offboarding will be removed. This cannot be undone.',
      positiveText: 'Cancel offboarding',
    });
    if (!confirmed) return;
    cancel.mutate(offboarding.id);
  };

  if (offboarding.completedAt || offboarding.canceledAt) return null;

  // ──────────────────────────────── IN PROGRESS ────────────────────────────────
  if (showInProgress) {
    return (
      <Box
        m={2}
        role="status"
        aria-live="polite"
        sx={(theme: Theme) => ({
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '14px',
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.warning.light,
            0.55,
          )} 0%, #ffffff 100%)`,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.55)}`,
          animation: `${enterIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both`,
        })}>
        <Stack p="16px 18px" gap={1.25}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              aria-hidden
              sx={(theme: Theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: alpha(theme.palette.warning.dark, 0.12),
                color: theme.palette.warning.dark,
                animation: `${spin} 1.6s linear infinite`,
              })}>
              <AutorenewRounded sx={{ fontSize: 16 }} />
            </Box>
            <Typography
              variant="overline"
              sx={theme => ({ color: theme.palette.warning.dark })}>
              In progress
            </Typography>
          </Stack>
          <Stack gap={0.25}>
            <Typography variant="h5">Offboarding in motion</Typography>
            <Typography variant="body2">
              {pollExhausted
                ? 'Still working on it. Refresh in a minute to see the latest status.'
                : 'Wrapping things up. This page will update on its own.'}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (!isFutureScheduled || !liveMs) return null;

  // ──────────────────────────────── SCHEDULED ────────────────────────────────
  const dateLabel = format(new Date(liveMs), 'EEE, MMM d');
  const timeLabel = format(new Date(liveMs), 'h:mm a');
  const relativeLabel = formatDistanceToNow(new Date(liveMs), {
    addSuffix: true,
  });

  return (
    <Box
      m={2}
      sx={(theme: Theme) => ({
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '14px',
        background: '#ffffff',
        border: `1px solid ${theme.palette.darkerLightGrey.main}`,
        boxShadow: `0 6px 20px -16px ${alpha(
          theme.palette.primary.main,
          0.18,
        )}`,
        animation: `${enterIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both`,
      })}>
      <Box
        aria-hidden
        sx={(theme: Theme) => ({
          position: 'absolute',
          right: -40,
          top: -40,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `radial-gradient(circle at center, ${alpha(
            theme.palette.primary.main,
            0.08,
          )} 0%, transparent 70%)`,
          pointerEvents: 'none',
        })}
      />
      <Stack p="16px 18px" gap={1.5} sx={{ position: 'relative' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box
            aria-hidden
            sx={(theme: Theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            })}>
            <EventOutlined sx={{ fontSize: 14 }} />
          </Box>
          <Typography
            variant="overline"
            sx={theme => ({ color: theme.palette.primary.main })}>
            Offboarding scheduled
          </Typography>
          <OffboardingCampaignActionMenu
            offboarding={offboarding}
            employeeName={employeeName}
          />
        </Stack>

        <Stack gap={0.5}>
          <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
            <Typography variant="h5">{dateLabel}</Typography>
            <Typography
              variant="body2"
              sx={() => ({ color: 'text.secondary' })}>
              · {timeLabel}
            </Typography>
          </Stack>
          {inRaceWindow ? (
            <Stack direction="row" alignItems="center" gap={1} mt={0.25}>
              <Box
                aria-hidden
                sx={(theme: Theme) => ({
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: theme.palette.primary.main,
                  animation: `${pulseDot} 1.6s ease-out infinite`,
                })}
              />
              <Typography
                variant="subtitle2"
                sx={theme => ({
                  color: theme.palette.primary.main,
                  fontVariantNumeric: 'tabular-nums',
                })}>
                Starting any moment ·{' '}
                {formatDistanceStrict(liveMs, now, { unit: 'second' })}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={() => ({ color: 'text.secondary' })}>
              {relativeLabel.charAt(0).toUpperCase() + relativeLabel.slice(1)}
            </Typography>
          )}
        </Stack>

        {!inRaceWindow ? (
          <Stack direction="row" gap={1}>
            <Button variant="light" color="info" onClick={onEdit}>
              Edit
            </Button>
            <Button
              variant="light"
              color="info"
              onClick={onCancel}
              disabled={cancel.isPending}>
              Cancel
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

export default OffboardingBanner;
